// Contrast segmentation runs on a bounded preview; returned rectangles use source pixels.
export function detectImageParts(
  { data, width, height },
  threshold = 28,
  maxParts = 7
) {
  if (!width || !height || data.length !== width * height * 4)
    throw new Error("Érvénytelen kép.");
  const count = width * height;
  const rgb = (id) => {
    const a = data[id * 4 + 3] / 255;
    return [0, 1, 2].map((c) => data[id * 4 + c] * a + 255 * (1 - a));
  };
  // Use the dominant border colour rather than assuming white or sampling a corner shadow.
  const buckets = new Map();
  const sample = (id) => {
    const color = rgb(id),
      key = color.map((v) => Math.floor(v / 24)).join(",");
    const bin = buckets.get(key) || { n: 0, sum: [0, 0, 0] };
    bin.n++;
    color.forEach((v, c) => {
      bin.sum[c] += v;
    });
    buckets.set(key, bin);
  };
  for (let x = 0; x < width; x++) {
    sample(x);
    sample((height - 1) * width + x);
  }
  for (let y = 1; y < height - 1; y++) {
    sample(y * width);
    sample(y * width + width - 1);
  }
  const background = [...buckets.values()].sort((a, b) => b.n - a.n)[0];
  const color = background.sum.map((v) => v / background.n);
  const mask = new Uint8Array(count);
  const light = new Float32Array(count);
  for (let id = 0; id < count; id++) {
    const pixel = rgb(id);
    light[id] = (pixel[0] + pixel[1] + pixel[2]) / 3;
    if (data[id * 4 + 3] < 32) continue;
    const distance = Math.sqrt(
      pixel.reduce((sum, v, c) => sum + (v - color[c]) ** 2, 0) / 3
    );
    mask[id] = distance >= threshold ? 1 : 0;
  }
  // Remove one-pixel bridges from soft shadows, then restore that pixel in the bounds.
  const core = new Uint8Array(count);
  for (let y = 1; y < height - 1; y++)
    for (let x = 1; x < width - 1; x++) {
      const id = y * width + x;
      core[id] =
        mask[id] &&
        mask[id - 1] &&
        mask[id + 1] &&
        mask[id - width] &&
        mask[id + width];
    }
  const queue = new Int32Array(count),
    candidates = [];
  for (let id = 0; id < count; id++) {
    if (!core[id]) continue;
    let head = 0,
      tail = 1,
      x0 = width,
      y0 = height,
      x1 = 0,
      y1 = 0;
    queue[0] = id;
    core[id] = 0;
    while (head < tail) {
      const next = queue[head++],
        x = next % width,
        y = Math.floor(next / width);
      x0 = Math.min(x0, x);
      x1 = Math.max(x1, x);
      y0 = Math.min(y0, y);
      y1 = Math.max(y1, y);
      for (const neighbor of [
        x > 0 ? next - 1 : -1,
        x + 1 < width ? next + 1 : -1,
        y > 0 ? next - width : -1,
        y + 1 < height ? next + width : -1
      ]) {
        if (neighbor >= 0 && core[neighbor]) {
          core[neighbor] = 0;
          queue[tail++] = neighbor;
        }
      }
    }
    const w = x1 - x0 + 3,
      h = y1 - y0 + 3;
    if (
      w < Math.max(6, width * 0.012) ||
      h < Math.max(6, height * 0.012) ||
      w * h < count * 0.0015 ||
      tail < Math.max(12, count * 0.00015)
    )
      continue;
    candidates.push({
      rect: [
        Math.max(0, x0 - 1),
        Math.max(0, y0 - 1),
        Math.min(width, x1 + 2),
        Math.min(height, y1 + 2)
      ],
      area: w * h
    });
  }
  // A panel's bevels and inner decoration are not separate panels.
  candidates.sort((a, b) => b.area - a.area);
  const parts = [];
  for (const candidate of candidates) {
    const r = candidate.rect;
    if (
      !parts.some(
        ({ rect: b }) =>
          r[0] >= b[0] && r[1] >= b[1] && r[2] <= b[2] && r[3] <= b[3]
      )
    )
      parts.push(candidate);
  }
  // Touching panels may share a dark joint instead of a background-coloured gap.
  // Split only seams running across almost the whole candidate, not short internal bevels.
  const splitSeam = (rect) => {
    let best = null;
    for (const axis of [0, 1]) {
      const low = rect[axis],
        high = rect[axis + 2],
        start = rect[1 - axis],
        end = rect[3 - axis];
      const margin = Math.max(6, Math.ceil((high - low) * 0.04)),
        radius = 3;
      for (let at = low + margin; at < high - margin; at++) {
        let hits = 0,
          strength = 0;
        for (let cross = start; cross < end; cross++) {
          const id = axis === 0 ? cross * width + at : at * width + cross;
          const step = axis === 0 ? radius : radius * width;
          const difference =
            Math.min(light[id - step], light[id + step]) - light[id];
          if (difference >= Math.max(10, threshold * 0.5)) {
            hits++;
            strength += difference;
          }
        }
        const ratio = hits / (end - start);
        if (ratio >= 0.88 && (!best || strength / (end - start) > best.score))
          best = { axis, at, score: strength / (end - start) };
      }
    }
    if (!best) return null;
    const a = [...rect],
      b = [...rect];
    a[best.axis + 2] = best.at;
    b[best.axis] = best.at;
    return [a, b];
  };
  const pending = parts.map((p) => p.rect),
    separated = [];
  while (pending.length) {
    const rect = pending.shift();
    const split =
      pending.length + separated.length < 30 ? splitSeam(rect) : null;
    if (split) pending.push(...split);
    else separated.push(rect);
  }
  const limit = Math.max(1, Math.min(7, maxParts));
  separated.sort(
    (a, b) => (b[2] - b[0]) * (b[3] - b[1]) - (a[2] - a[0]) * (a[3] - a[1])
  );
  return {
    total: separated.length,
    rects: separated.slice(0, limit).sort((a, b) => a[1] - b[1] || a[0] - b[0])
  };
}

export function detectPartsFromImage(image, threshold) {
  const scale = Math.min(
    1,
    1000 / Math.max(image.naturalWidth, image.naturalHeight)
  );
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
  canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
  const context = canvas.getContext("2d", { willReadFrequently: true });
  if (!context)
    throw new Error("A böngésző nem tudta elindítani a képfelismerést.");
  context.drawImage(image, 0, 0, canvas.width, canvas.height);
  const result = detectImageParts(
    context.getImageData(0, 0, canvas.width, canvas.height),
    threshold
  );
  return {
    ...result,
    rects: result.rects.map((r) =>
      r.map((v, i) => {
        const ratio =
          i % 2 === 0
            ? image.naturalWidth / canvas.width
            : image.naturalHeight / canvas.height;
        return i < 2 ? Math.floor(v * ratio) : Math.ceil(v * ratio);
      })
    )
  };
}
